(ns renolit-roofing-service.shared.domain.service
  (:require
   [malli.core :as m]
   [malli.util :as mu]
   [renolit-roofing-service.shared.domain.miscellaneous :as dom.misc]
   [renolit-roofing-service.shared.domain.types :as dom.types]))

(def polygon
  [:vector [:tuple :double :double]])

(def zones
  [:vector {:min 1}
   [:map
    [:id :uuid]
    [:name :string]
    [:area polygon]
    [:subzones [:set :uuid]]]])

(def tasks
  [:vector {:min 1}
   [:map
    [:task-type (dom.types/get-type-schema :task-type)]
    [:comment
     [:string {:min 1}]]]])

(def common-review
  [:map
   [:id :uuid]
   [:type (dom.types/get-type-schema :revision-type)]
   [:zone-id :uuid]
   [:wp (dom.types/get-type-schema :wp)]
   [:wp-type (dom.types/get-type-schema :wp-type)]])

(def general-review
  (mu/merge
   common-review
   [:map
    [:tasks tasks]]))

(def focused-review
  (mu/merge
   common-review
   [:map
    [:tasks (update-in tasks [1] assoc :max 1)]
    [:lat-lng [:tuple :double :double]]]))

(def review
  [:multi {:dispatch :type}
   [:general general-review]
   [:focused focused-review]])

(def reviews
  [:vector {:min 1} review])

(def common-observation
  [:map
   [:id :uuid]
   [:ui-id :string] ; for 125_00001 external id
   [:review-id :uuid]
   [:wp (dom.types/get-type-schema :wp)]
   [:task-type (dom.types/get-type-schema :task-type)]
   [:clear :boolean]
   [:lat-lng {:optional true} [:tuple :double :double]]
   [:description :string]
   [:images [:vector
             [:map
              [:photo-id :string]
              [:object-id :string]]]]  ; will refer to ids in object storage
   [:created-at {:optional true} dom.misc/instant-schema]])

(def common-clear-observation
  (-> common-observation
      (mu/dissoc :lat-lng)
      (mu/dissoc :description)
      (mu/dissoc :images)))

(def visual-inspection-observation
  (mu/merge
   common-observation
   [:map
    [:observation (dom.types/get-type-schema :observation)]
    [:incidence-level (dom.types/get-type-schema :incidence-level)]]))

(def non-destructive-testing-observation
  (mu/merge
   common-observation
   [:map
    [:dirt-type (dom.types/get-type-schema :dirt-types)]
    [:photo-position (dom.types/get-type-schema :photo-position)]
    [:face (dom.types/get-type-schema :face)]]))

(def observation
  [:multi {:dispatch :clear}
   [true common-clear-observation]
   [false [:multi {:dispatch :wp}
           [:visual-inspection visual-inspection-observation]
           [:non-destructive-testing non-destructive-testing-observation]
           [:destructive-testing common-observation]]]])

(def observations
  [:vector observation])

(def analysis
  [:map
   [:id :uuid]
   [:observation-id :uuid]
   [:image-id :string]
   [:valid :boolean]
   [:crack-level [:int {:min 1 :max 9}]]
   [:observations :string]
   [:request-repetition :boolean]])

(def analyses
  [:vector analysis])

(def conclusions
  [:map
   [:comments [:vector [:map
                        [:observation-id :uuid]
                        [:comment :string]]]]
   [:visual-inspection [:maybe :string]]
   [:non-destructive-testing [:maybe :string]]
   [:destructive-testing [:maybe :string]]
   [:final [:maybe :string]]
   [:membrane-life :int]])

(def service-schema
  (m/schema
   [:map
    [:id :uuid]
    [:project-id [:uuid]]
    [:name [:string]]
    [:origin (dom.types/get-type-schema :service-origin)]
    [:reason (dom.types/get-type-schema :service-reason)]
    [:reason-text [:string]]
    [:objective [:string]]
    [:manager [:string]]
    [:inspector [:string]]
    [:authorized-diagnostic-contractor [:string]]
    [:last-diagnosis-date dom.misc/instant-schema]
    [:last-maintenance-date dom.misc/instant-schema]
    [:customer-name [:string]] ; customer
    [:customer-profile (dom.types/get-type-schema :customer-profile)]
    [:company-owner [:string]]
    [:company-industry [:string]]
    [:building-height [:double]] ; building
    [:roof-slope [:string]]
    [:upstand [:double]]
    [:service-area [:double]]
    [:roofing-membrane [:string]] ; roof structure
    [:application-method [:string]]
    [:support [:string]]
    [:protective-glue [:string]]
    [:vapor-barrier [:string]]
    [:glue-type [:string]]
    [:thermal-insulation [:string]]
    [:separation-layer [:string]]
    [:color [:string]]
    [:fixation [:string]]
    [:status (dom.types/get-type-schema :service-status)]
    [:zones zones] ; zones json
    [:reviews reviews] ; reviews/wp json
    [:observations observations] ; observations added by installer
    [:analyses analyses] ; analyses of the observations by lab
    [:conclusions {:optional true} conclusions] ; conclusions added by lab after checking analyses
    [:created-at dom.misc/instant-schema]
    [:updated-at dom.misc/instant-schema]]))

(def create-service-schema
  (-> service-schema
      (mu/dissoc :id)
      (mu/dissoc :status)
      (mu/dissoc :observations)
      (mu/dissoc :analyses)
      (mu/dissoc :conclusions)
      (mu/dissoc :created-at)
      (mu/dissoc :updated-at)))

(def update-service-schema
  (-> create-service-schema
      (mu/dissoc :zones)
      (mu/dissoc :reviews)))
